import { ICS2NotificationError } from 'store/declarations/eu/ics2/ics2-notification';
import { formatIcs2FieldPath } from 'views/declarations/eu/ics2/errors/formatFieldPath';
import { formatIcs2ErrorLocation } from 'views/declarations/eu/ics2/errors/formatLocation';
import { parseIcs2EnsMessageElementPath } from 'views/declarations/eu/ics2/errors/parseMessageElementPath';
import { compareIcs2ErrorLocations } from 'views/declarations/eu/ics2/errors/sortLocations';
import { Ics2ErrorLocation } from 'views/declarations/eu/ics2/errors/types';
import { ErrorCardSection } from '../error-cards/groupErrorCardsByItem';
import { ErrorCard, ErrorCardField } from '../error-cards/types';

const OTHER_ERRORS_TITLE = 'Other errors';

/** A house item's errors are read with its consignment, so the section names only the consignment. */
const getSectionTitle = (location?: Ics2ErrorLocation): string => {
    const sectionLocation: Ics2ErrorLocation | undefined =
        location?.level === 'houseItem' ? { ...location, level: 'houseConsignment' } : location;

    return formatIcs2ErrorLocation(sectionLocation) ?? OTHER_ERRORS_TITLE;
};

/** Only an error we can name a place for gets a link. A house item is named, as its section is not. */
const getFields = (location?: Ics2ErrorLocation): ErrorCardField[] => {
    const itemName = location?.level === 'houseItem' ? `Item ${location.itemIndex}` : undefined;
    const fieldName = formatIcs2FieldPath(location?.fieldPath);
    const label = [itemName, fieldName].filter(Boolean).join(' - ');

    return label ? [{ label, formPath: location?.fieldPath ?? '' }] : [];
};

/**
 * The errors of a rejected ENS in the order the declaration reads: the master level, its items, then
 * each house consignment followed by its own items. There is one section per master item and per house
 * consignment. The sort is stable, so errors of one level keep the order customs sent them in, and those
 * whose pointer cannot be read come last.
 *
 * The numbers run across the sections, so they still add up to the total.
 */
const getIcs2ErrorSections = (
    errors: ICS2NotificationError[] | undefined,
    getFunctionalErrorLabel: (validationCode?: string) => string | undefined
): ErrorCardSection<ICS2NotificationError>[] => {
    const locatedErrors = (errors ?? [])
        .map((error) => ({ error, location: parseIcs2EnsMessageElementPath(error.pointer?.messageElementPath) }))
        .sort((a, b) => compareIcs2ErrorLocations(a.location, b.location));

    const sectionsByTitle = new Map<string, ErrorCardSection<ICS2NotificationError>>();

    locatedErrors.forEach(({ error, location }, index) => {
        const card: ErrorCard<ICS2NotificationError> = {
            // The code's meaning, as customs often sends only the code. The code itself stays in the details.
            code: getFunctionalErrorLabel(error.validationCode),
            // Customs' own explanation, as under "What happened" in the details. Without one the line above
            // already says all there is, so it is not repeated.
            message: error.description ?? undefined,
            fields: getFields(location),
            related: [],
            error,
        };

        const title = getSectionTitle(location);
        const section = sectionsByTitle.get(title) ?? { title, cards: [] };

        section.cards.push({ card, number: index + 1 });
        sectionsByTitle.set(title, section);
    });

    return Array.from(sectionsByTitle.values());
};

export default getIcs2ErrorSections;
